import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import RiskProfiler from './RiskProfiler';

const { FiPlus, FiTrash2, FiArrowRight, FiUser, FiEdit, FiHelpCircle, FiCheckCircle } = FiIcons;

const GoalInput = ({ goals, addGoal, removeGoal, updateGoal, userProfile, updateUserProfile, proceedToResults }) => {
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [showRiskProfiler, setShowRiskProfiler] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [goalForm, setGoalForm] = useState({
    name: '',
    yearsFromNow: '',
    currentCost: '',
    expectedReturn: '',
    currentlyInvesting: false,
    currentSIP: '',
    currentFunds: ''
  });

  const goalOptions = [
    'Child Education',
    'Retirement',
    'Own House',
    'Debt Clearance',
    'Marriage',
    'Emergency Fund',
    'Vacation',
    'Car Purchase',
    'Home Renovation',
    'Other'
  ];

  const handleGoalSubmit = (e) => {
    e.preventDefault();
    if (goalForm.name && goalForm.yearsFromNow && goalForm.currentCost && goalForm.expectedReturn) {
      if (editingGoalId) {
        updateGoal(editingGoalId, goalForm);
        setEditingGoalId(null);
      } else {
        addGoal(goalForm);
      }
      setGoalForm({
        name: '',
        yearsFromNow: '',
        currentCost: '',
        expectedReturn: '',
        currentlyInvesting: false,
        currentSIP: '',
        currentFunds: ''
      });
      setShowGoalForm(false);
    }
  };

  const handleEditGoal = (goal) => {
    setGoalForm({
      name: goal.name,
      yearsFromNow: goal.yearsFromNow,
      currentCost: goal.currentCost,
      expectedReturn: goal.expectedReturn,
      currentlyInvesting: goal.currentlyInvesting || false,
      currentSIP: goal.currentSIP || '',
      currentFunds: goal.currentFunds || ''
    });
    setEditingGoalId(goal.id);
    setShowGoalForm(true);
  };

  const handleCancelEdit = () => {
    setGoalForm({
      name: '',
      yearsFromNow: '',
      currentCost: '',
      expectedReturn: '',
      currentlyInvesting: false,
      currentSIP: '',
      currentFunds: ''
    });
    setEditingGoalId(null);
    setShowGoalForm(false);
  };

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    setShowProfileForm(false);
  };

  const handleRiskProfileComplete = (result) => {
    updateUserProfile({ ...userProfile, riskComfort: result });
    setShowRiskProfiler(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      {/* Risk Profiler Modal */}
      {showRiskProfiler && (
        <RiskProfiler onComplete={handleRiskProfileComplete} onClose={() => setShowRiskProfiler(false)} />
      )}

      {/* User Profile Section */}
      <motion.div
        id="profile-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <SafeIcon icon={FiUser} className="text-xl sm:text-2xl text-indigo-600" />
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Your Profile</h2>
          </div>
          <button
            onClick={() => setShowProfileForm(!showProfileForm)}
            className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm sm:text-base"
          >
            {userProfile.age ? 'Edit Profile' : 'Add Profile'}
          </button>
        </div>

        {userProfile.age && !showProfileForm && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
            <div className="bg-gray-50 px-3 py-2 rounded-lg">
              <span className="text-gray-600">Age:</span> <span className="font-medium">{userProfile.age} years</span>
            </div>
            <div className="bg-gray-50 px-3 py-2 rounded-lg flex items-center space-x-2">
              <span className="text-gray-600">Risk Comfort:</span>
              <span
                className={`font-bold px-2 py-0.5 rounded text-xs ${
                  userProfile.riskComfort === 'High'
                    ? 'bg-red-100 text-red-800'
                    : userProfile.riskComfort === 'Low'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {userProfile.riskComfort}
              </span>
            </div>
            {userProfile.monthlyIncome && (
              <div className="bg-gray-50 px-3 py-2 rounded-lg">
                <span className="text-gray-600">Monthly Income:</span>{' '}
                <span className="font-medium">₹{userProfile.monthlyIncome}</span>
              </div>
            )}
            {userProfile.existingInvestments && (
              <div className="bg-gray-50 px-3 py-2 rounded-lg">
                <span className="text-gray-600">Existing MF Investments:</span>{' '}
                <span className="font-medium">₹{userProfile.existingInvestments}</span>
              </div>
            )}
          </div>
        )}

        <AnimatePresence>
          {showProfileForm && (
            <motion.form
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              onSubmit={handleProfileSubmit}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Age *</label>
                <input
                  type="number"
                  value={userProfile.age}
                  onChange={(e) => updateUserProfile({ ...userProfile, age: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                  placeholder="35"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Risk Comfort</label>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                  <select
                    value={userProfile.riskComfort}
                    onChange={(e) => updateUserProfile({ ...userProfile, riskComfort: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                  >
                    <option value="Low">Low (Conservative)</option>
                    <option value="Medium">Medium (Moderate)</option>
                    <option value="High">High (Aggressive)</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowRiskProfiler(true)}
                    className="px-3 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors flex items-center justify-center space-x-1 whitespace-nowrap text-sm font-medium"
                    title="Take Risk Quiz"
                  >
                    <SafeIcon icon={FiHelpCircle} />
                    <span>Analyze Risk</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Income (₹)</label>
                <input
                  type="number"
                  value={userProfile.monthlyIncome}
                  onChange={(e) => updateUserProfile({ ...userProfile, monthlyIncome: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                  placeholder="100000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Existing MF Investments (₹)</label>
                <input
                  type="number"
                  value={userProfile.existingInvestments}
                  onChange={(e) => updateUserProfile({ ...userProfile, existingInvestments: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                  placeholder="500000"
                />
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Save Profile
                </button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Goals Section */}
      <motion.div
        id="goals-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-white rounded-xl shadow-lg p-4 sm:p-6"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Your Financial Goals</h2>
          <button
            onClick={() => {
              if (!showGoalForm) {
                setEditingGoalId(null);
                setGoalForm({
                  name: '',
                  yearsFromNow: '',
                  currentCost: '',
                  expectedReturn: '',
                  currentlyInvesting: false,
                  currentSIP: '',
                  currentFunds: ''
                });
              }
              setShowGoalForm(!showGoalForm);
            }}
            className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} />
            <span>Add Goal</span>
          </button>
        </div>

        {/* Goal Form */}
        <AnimatePresence>
          {showGoalForm && (
            <motion.form
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              onSubmit={handleGoalSubmit}
              className="bg-gray-50 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  {editingGoalId ? 'Edit Goal' : 'Add New Goal'}
                </h3>
                {editingGoalId && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="text-xs sm:text-sm text-gray-600 hover:text-gray-800"
                  >
                    Cancel Edit
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Goal Name *</label>
                  <select
                    value={goalForm.name}
                    onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                    required
                  >
                    <option value="">Select a goal</option>
                    {goalOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Years From Now *</label>
                  <input
                    type="number"
                    value={goalForm.yearsFromNow}
                    onChange={(e) => setGoalForm({ ...goalForm, yearsFromNow: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                    placeholder="10"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Today's Cost (₹) *</label>
                  <input
                    type="number"
                    value={goalForm.currentCost}
                    onChange={(e) => setGoalForm({ ...goalForm, currentCost: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                    placeholder="1000000"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Return (%) *</label>
                  <input
                    type="number"
                    step="0.1"
                    value={goalForm.expectedReturn}
                    onChange={(e) => setGoalForm({ ...goalForm, expectedReturn: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                    placeholder="12"
                    required
                  />
                </div>

                <div className="sm:col-span-2">
                  <div className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      id="currentlyInvesting"
                      checked={goalForm.currentlyInvesting}
                      onChange={(e) => setGoalForm({ ...goalForm, currentlyInvesting: e.target.checked })}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="currentlyInvesting" className="ml-2 text-sm text-gray-700">
                      Already investing for this goal?
                    </label>
                  </div>

                  {goalForm.currentlyInvesting && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Current Monthly SIP (₹)</label>
                        <input
                          type="number"
                          value={goalForm.currentSIP}
                          onChange={(e) => setGoalForm({ ...goalForm, currentSIP: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                          placeholder="5000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Current Funds/Category</label>
                        <input
                          type="text"
                          value={goalForm.currentFunds}
                          onChange={(e) => setGoalForm({ ...goalForm, currentFunds: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                          placeholder="Large-cap, ELSS, etc."
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="sm:col-span-2 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    {editingGoalId ? 'Update Goal' : 'Add Goal'}
                  </button>
                  {editingGoalId && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="w-full sm:w-auto px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Goals List */}
        <div className="space-y-3 sm:space-y-4">
          <AnimatePresence>
            {goals.map((goal) => (
              <motion.div
                key={goal.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-3 sm:p-4 border border-indigo-200"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base truncate">{goal.name}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                      <div className="truncate">
                        <span className="font-medium">Timeline:</span> {goal.yearsFromNow} years
                      </div>
                      <div className="truncate">
                        <span className="font-medium">Cost:</span> ₹{parseInt(goal.currentCost).toLocaleString('en-IN')}
                      </div>
                      <div className="truncate">
                        <span className="font-medium">Return:</span> {goal.expectedReturn}%
                      </div>
                    </div>
                    {goal.currentlyInvesting && (
                      <div className="mt-2 text-xs sm:text-sm text-indigo-600 truncate">
                        Currently investing ₹{goal.currentSIP}/month in {goal.currentFunds}
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2 ml-2 flex-shrink-0">
                    <button
                      onClick={() => handleEditGoal(goal)}
                      className="text-blue-500 hover:text-blue-700 transition-colors p-1"
                      title="Edit Goal"
                    >
                      <SafeIcon icon={FiEdit} className="text-lg sm:text-xl" />
                    </button>
                    <button
                      onClick={() => removeGoal(goal.id)}
                      className="text-red-500 hover:text-red-700 transition-colors p-1"
                      title="Delete Goal"
                    >
                      <SafeIcon icon={FiTrash2} className="text-lg sm:text-xl" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {goals.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-base sm:text-lg mb-2">No goals added yet</p>
            <p className="text-sm">Add your first financial goal to get started with the simulation</p>
          </div>
        )}

        {goals.length > 0 && (
          <div id="simulate-button" className="mt-6 sm:mt-8 text-center">
            <button
              onClick={proceedToResults}
              className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 px-6 sm:px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-md"
            >
              <span>Simulate Results</span>
              <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default GoalInput;